import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  FileText,
  Home,
  Landmark,
  Plus,
  ReceiptText,
  ShieldCheck,
  Trash2,
  UserRound,
  Wallet,
} from "lucide-react";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createReminder,
  listProperties,
  listUpcomingReminders,
  removeReminder,
  updateReminder,
} from "@/services/dataService";
import { formatDate, toDateInputValue } from "@/lib/format";

const DAY_MS = 24 * 60 * 60 * 1000;

const KIND_ICONS = {
  lease: UserRound,
  license: FileText,
  insurance: ShieldCheck,
  document: FileText,
  income: Wallet,
  expense: ReceiptText,
  mortgage: Landmark,
  custom: CalendarClock,
};

const SEVERITY_STYLES = {
  overdue: {
    badge: "destructive",
    label: "Overdue",
    ring: "bg-rose-500/10 text-rose-600 dark:text-rose-300",
  },
  urgent: {
    badge: "destructive",
    label: "Due this week",
    ring: "bg-rose-500/10 text-rose-600 dark:text-rose-300",
  },
  soon: {
    badge: "secondary",
    label: "Due this month",
    ring: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
  },
  upcoming: {
    badge: "outline",
    label: "Upcoming",
    ring: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
  },
};

function monthHeading(timestamp) {
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function daysLeftLabel(dueAt) {
  const days = Math.floor((dueAt - Date.now()) / DAY_MS);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  return `in ${days}d`;
}

const EMPTY_FORM = {
  title: "",
  notes: "",
  propertyId: "",
  dueAt: toDateInputValue(Date.now() + 7 * DAY_MS),
  leadDays: "7",
};

export default function CalendarPage() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [horizon, setHorizon] = useState(120);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const remindersQuery = useQuery({
    queryKey: ["upcomingReminders", userId, horizon],
    queryFn: () => listUpcomingReminders(horizon),
    enabled: Boolean(userId),
  });

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(),
    enabled: Boolean(userId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["upcomingReminders", userId] });

  const createMutation = useMutation({
    mutationFn: createReminder,
    onSuccess: async () => {
      await invalidate();
      setIsAddOpen(false);
      setForm(EMPTY_FORM);
      toast({ title: "Reminder added" });
    },
    onError: (error) =>
      toast({ title: "Could not add reminder", description: error.message, variant: "destructive" }),
  });

  const completeMutation = useMutation({
    mutationFn: (id) => updateReminder({ id, isCompleted: true }),
    onSuccess: invalidate,
    onError: (error) =>
      toast({ title: "Could not complete reminder", description: error.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: removeReminder,
    onSuccess: invalidate,
    onError: (error) =>
      toast({ title: "Could not delete reminder", description: error.message, variant: "destructive" }),
  });

  const grouped = useMemo(() => {
    const groups = [];
    let currentKey = null;
    for (const item of remindersQuery.data || []) {
      const key = monthHeading(item.dueAt);
      if (key !== currentKey) {
        groups.push({ key, items: [] });
        currentKey = key;
      }
      groups[groups.length - 1].items.push(item);
    }
    return groups;
  }, [remindersQuery.data]);

  const counts = useMemo(() => {
    const items = remindersQuery.data || [];
    return {
      overdue: items.filter((item) => item.severity === "overdue").length,
      urgent: items.filter((item) => item.severity === "urgent").length,
      total: items.length,
    };
  }, [remindersQuery.data]);

  return (
    <PageLayout
      title="Calendar"
      eyebrow="Deadlines"
      subtitle="Mortgage drafts, lease renewals, license expirations, insurance due dates, and your own reminders — nothing slips."
      onRefresh={() => remindersQuery.refetch()}
      isRefreshing={remindersQuery.isFetching}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {[60, 120, 365].map((days) => (
              <Button
                key={days}
                size="sm"
                variant={horizon === days ? "default" : "outline"}
                onClick={() => setHorizon(days)}
              >
                Next {days >= 365 ? "12 months" : `${days} days`}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {counts.overdue > 0 ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-rose-600 dark:text-rose-300">
                <AlertTriangle className="h-4 w-4" />
                {counts.overdue} overdue
              </span>
            ) : null}
            <Button onClick={() => setIsAddOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Add reminder
            </Button>
          </div>
        </div>

        {grouped.map((group) => (
          <section key={group.key}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {group.key}
            </h2>
            <div className="space-y-3">
              {group.items.map((item) => {
                const Icon = KIND_ICONS[item.kind] || Home;
                const severity = SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.upcoming;
                return (
                  <Card key={item.key}>
                    <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${severity.ring}`}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.propertyName ? `${item.propertyName} · ` : ""}
                            {item.subtitle}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3">
                        <Badge variant={severity.badge}>{daysLeftLabel(item.dueAt)}</Badge>
                        <span className="text-sm tabular-nums text-muted-foreground">
                          {formatDate(item.dueAt)}
                        </span>
                        {item.isCustom ? (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-emerald-600"
                              aria-label="Mark complete"
                              disabled={completeMutation.isPending}
                              onClick={() => completeMutation.mutate(item.sourceId)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-rose-600"
                              aria-label="Delete reminder"
                              disabled={deleteMutation.isPending}
                              onClick={() => deleteMutation.mutate(item.sourceId)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        ))}

        {!remindersQuery.isLoading && !grouped.length ? (
          <Card>
            <CardContent className="py-14 text-center">
              <CalendarClock className="mx-auto h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 font-medium">Nothing due in this window</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Mortgage drafts, lease deadlines, licenses, insurance, documents, and recurring
                entries appear here automatically.
              </p>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add reminder</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Title</Label>
              <Input
                placeholder="County inspection, tax filing, gutter cleaning..."
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Due date</Label>
                <Input
                  type="date"
                  value={form.dueAt}
                  onChange={(event) => setForm((prev) => ({ ...prev, dueAt: event.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Remind me (days before)</Label>
                <Input
                  type="number"
                  min="0"
                  max="120"
                  value={form.leadDays}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, leadDays: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Property (optional)</Label>
              <select
                value={form.propertyId}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, propertyId: event.target.value }))
                }
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Portfolio-level</option>
                {(propertiesQuery.data || []).map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name || property.address}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                createMutation.mutate({
                  title: form.title.trim(),
                  notes: form.notes.trim() || undefined,
                  propertyId: form.propertyId || undefined,
                  dueAt: new Date(`${form.dueAt}T12:00:00`).getTime(),
                  leadDays: Number(form.leadDays) || 7,
                })
              }
              disabled={createMutation.isPending || !form.title.trim() || !form.dueAt}
            >
              {createMutation.isPending ? "Saving..." : "Add reminder"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
